import React, { useState, useEffect, useRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FaEdit, FaTrashAlt, FaEye } from "react-icons/fa";
import AddEditCountryModal from "./addEditCountry";
import BSPagination from "../../Components/pagination";
import LoadingSpinner from "../../Components/spinner";
import {
  deleteCountryById,
  fetchCountriesList,
  restoreCountry,
  searchCountries,
  toggleCountryStatus,
} from "../../services/axios/apiManager";
import CountryDetails from "./countryDetails";
import { useNavigate } from "react-router-dom";
import { handleErrors } from "../../services/dataManager";
import { toast } from "sonner";
import $ from "jquery";
import "datatables.net-bs5";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";

const CountriesList = ({ granted, countries, setCountries, pagination, setPagination }) => {
  const navigate = useNavigate();
  const tableRef = useRef(null);

  const [query, setQuery] = useState("");
  const [filtering, setFiltering] = useState(false);
  const [filters, setFilters] = useState(3);
  const [modalOptions, setModalOptions] = useState({});
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState({});
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  const columns = [
    { data: "id", title: "ID" },
    { data: "country_name", title: "Name" },
    { data: "country_code", title: "Code" },
    { data: "iso3_code", title: "ISO-3 Code" },
    { data: "numeric_code", title: "Numeric Code" },
    { data: "capital", title: "Capital" },
    { data: "native_name", title: "Native Name" },
  ];

  const fetchCountries = async (pgntn, str = "") => {
    try {
      setFiltering(true);
      const res = await fetchCountriesList(pgntn.currentPage, pgntn.pageSize, str);
      if (res?.data?.success && res?.data?.data) {
        setCountries(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else {
        handleErrors(res, navigate, "Countries list:");
      }
    } catch (error) {
      handleErrors(error, navigate, "Countries list:");
    } finally {
      setFiltering(false);
    }
  };

  const handlePageChange = async (page) => {
    const temp = { ...pagination, currentPage: page };
    setPagination(temp);
    if (query) {
      setFiltering(true);
      const res = await searchCountries(query, temp.currentPage, temp.pageSize);
      if (res?.data?.data) {
        setCountries(res.data.data);
        res.data.pagination && setPagination(res.data.pagination);
      } else {
        handleErrors(res, navigate, "Search countries:");
      }
      setFiltering(false);
    } else {
      await fetchCountries(temp);
    }
  };

  const handleSearch = async (e) => {
    const string = e.target.value;
    setQuery(string);
    if (string) {
      setFiltering(true);
      try {
        const res = await searchCountries(string, 1, pagination.pageSize);
        if (res?.data?.data) {
          setCountries(res.data.data);
          res.data.pagination && setPagination(res.data.pagination);
        } else {
          handleErrors(res, navigate, "Search countries:");
        }
      } catch (error) {
        handleErrors(error, navigate, "Search countries:");
      } finally {
        setFiltering(false);
      }
    } else {
      await fetchCountries(pagination);
    }
  };

  const resetFilters = async () => {
    setFilters(3);
    setQuery("");
    await fetchCountries(pagination);
  };

  useEffect(() => {
    fetchCountries(pagination);
  }, []);

  const fetchFilteredCountries = async (e) => {
    const value = e.target.value;
    setFilters(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    const str = `status=${value}`;
    await fetchCountries({ ...pagination, currentPage: 1 }, str);
  };

  const handleOpenAddEditModal = (country) => {
    setSelectedCountry(country);
    setShowAddEditModal(true);
  };

  const handleOpenDetailsModal = (country) => {
    setSelectedCountry(country);
    setOpenDetailsModal(true);
  };

  const handleStatusChange = async (country) => {
    if (!granted?.some((g) => g.type === "toggle-status")) {
      return toast.error("You don't have necessary permissions!", {
        duration: 3000,
      });
    }
    setModalOptions({
      header: "Change Status",
      body: `Change status of "${country.country_name}"?`,
      okay: "Change",
      method: changeStatus,
      data: { id: country.id, status: country.status === 1 ? 0 : 1 },
    });
    setShowOptionsModal(true);
  };

  const changeStatus = async ({ id, status }) => {
    const res = await toggleCountryStatus({ id, status });
    if (res?.data?.success) {
      const str = `status=${filters}`;
      await fetchCountries(pagination, str);
    } else handleErrors(res, navigate, "Change Status:");
    setShowOptionsModal(false);
  };

  const handleDeleteCountry = (country) => {
    setModalOptions({
      header: "Delete Country",
      body: `Delete "${country.country_name}"?`,
      okay: "Delete",
      method: deleteCountry,
      data: { id: country.id },
    });
    setShowOptionsModal(true);
  };

  const deleteCountry = async ({ id }) => {
    const res = await deleteCountryById(id);
    if (res?.data?.success) {
      const str = `status=${filters}`;
      await fetchCountries(pagination, str);
      toast.info("Country record has been deleted!", {
        duration: 2000,
      });
    } else handleErrors(res, navigate, "Delete Country:");
    setShowOptionsModal(false);
  };

  const handleRestoreCountry = (country) => {
    setModalOptions({
      header: "Restore Country",
      body: `Do you want to restore "${country.country_name}"?`,
      okay: "Restore",
      method: restoreCountrybyId,
      data: { id: country.id },
    });
    setShowOptionsModal(true);
  };

  const restoreCountrybyId = async ({ id }) => {
    const res = await restoreCountry(id);
    if (res?.data?.success) {
      const str = `status=${filters}`;
      await fetchCountries(pagination, str);
      toast.info("Country record has been restored!", {
        duration: 2000,
      });
    } else handleErrors(res, navigate, "Delete Country:");
    setShowOptionsModal(false);
  };

  const handleCloseOptionsModal = () => {
    setShowOptionsModal(false);
  };
  useEffect(() => {
    // Prepare row data
    const tableData = countries.map((country) => {
      const row = columns.map((col) => country[col.data]);
      row.push(
        `<button class=\"btn btn-sm status-btn ${country.status === 1 ? "text-success" : "text-danger"}\" data-id=\"${country.id}\" title="change status">${country.status === 1 ? "Active" : "Inactive"}</button>`
      );
      row.push(
        (filters !== "2"
          ? `<button class=\"btn btn-outline-info btn-sm view-btn me-1\" data-id=\"${country.id}\"><i class=\"fas fa-eye\" title="view details"></i></button>`
          : "") +
        (granted?.some((g) => g.type === "update") && filters !== "2"
          ? `<button class=\"btn btn-outline-primary btn-sm edit-btn me-1\" data-id=\"${country.id}\"><i class=\"fas fa-edit\" title="edit"></i></button>`
          : "") +
        (granted?.some((g) => g.type === "delete") &&
          !country.is_associated &&
          country.status === 0 &&
          filters !== "2"
          ? `<button class=\"btn btn-outline-danger btn-sm delete-btn\" data-id=\"${country.id}\" title="delete"><i class=\"fas fa-trash\"></i></button>`
          : "") +
        (filters === "2"
          ? `<button class=\"btn btn-outline-danger btn-sm restore-btn\" data-id=\"${country.id}\" title="restore"><i class="fa-solid fa-rotate-right"></i></button>`
          : "")
      );
      return row;
    });

    const config = {
      data: tableData,
      columns: [
        { title: "ID", className: "text-start" },
        ...columns.slice(1).map((col) => ({ title: col.title })),
        { title: "Status", orderable: false },
        { title: "Actions", orderable: false },
      ],

      destroy: true,
      responsive: true,
      pageLength: pagination.pageSize || 10,
      paging: false,
      searching: false,
    };

    if ($.fn.dataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().clear().rows.add(tableData).draw();
    } else {
      $(tableRef.current).DataTable(config);
    }

    const $table = $(tableRef.current);
    $table.off("click", ".view-btn").on("click", ".view-btn", function () {
      const id = $(this).data("id");
      const country = countries.find((c) => c.id === id);
      handleOpenDetailsModal(country);
    });
    $table.off("click", ".edit-btn").on("click", ".edit-btn", function () {
      const id = $(this).data("id");
      const country = countries.find((c) => c.id === id);
      handleOpenAddEditModal(country);
    });
    $table.off("click", ".delete-btn").on("click", ".delete-btn", function () {
      const id = $(this).data("id");
      const country = countries.find((c) => c.id === id);
      handleDeleteCountry(country);
    });
    $table
      .off("click", ".restore-btn")
      .on("click", ".restore-btn", function () {
        const id = $(this).data("id");
        const country = countries.find((c) => c.id === id);
        handleRestoreCountry(country);
      });
    $table.off("click", ".status-btn").on("click", ".status-btn", function () {
      const id = $(this).data("id");
      const country = countries.find((c) => c.id === id);
      if (!country.deletedAt) {
        handleStatusChange(country);
      } else {
        toast.info("Cannot change status of a deleted record!", {
          duration: 2000,
        });
      }
    });

    return () => {
      $table.DataTable().destroy();
    };
  }, [countries, pagination.pageSize, granted]);

  return (
    <div className="bg-dark p-4 rounded mb-4">
      <div className="row align-items-center mb-4">
        <div className="col-md-8 row">
          <div className="col-md-5">
            <Form.Control
              type="text"
              placeholder="Search Countries..."
              value={query}
              onChange={handleSearch}
              className="bg-dark text-light border-light"
            />
          </div>
          <div className="col-md-5">
            <Form.Select
              value={filters}
              onChange={fetchFilteredCountries}
              className="bg-dark border-light text-light"
            >
              <option value="3">Active & Inactive</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
              <option className="text-danger" value="2">
                Deleted
              </option>
            </Form.Select>
          </div>
          <div className="col-md-2">
            {filtering ? (
              <LoadingSpinner size="md" />
            ) : (
              <Button
                title="Reset"
                className="btn btn-success"
                onClick={resetFilters}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
        <div className="col-md-4 text-end">
          {granted?.some((g) => g.type === "create") && (
            <Button
              className="btn btn-success"
              title="Add new country"
              onClick={() => handleOpenAddEditModal({})}
            >
              <i className="bi bi-plus-circle"></i> Add New
            </Button>
          )}
        </div>
      </div>

      <div className="table-responsive mt-4">
        <table
          ref={tableRef}
          className="table table-striped w-100 table-dark"
          style={{ whiteSpace: "nowrap" }}
        />
        <div className="d-flex justify-content-end mt-2">
          <div className="mx-4 py-2">
            <span className="text-light">{`Rows/Page ${pagination.pageSize || 0}, Total records ${pagination?.totalRecords || 0}`}</span>
          </div>
          <BSPagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            siblingCount={2}
            showFirstLast
          />
        </div>
      </div>

      <AddEditCountryModal
        showModal={showAddEditModal}
        setShowModal={setShowAddEditModal}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
        fetchCountries={fetchCountries}
      />

      <CountryDetails
        openDetailsModal={openDetailsModal}
        setOpenDetailsModal={setOpenDetailsModal}
        selectedCountry={selectedCountry}
        setSelectedCountry={setSelectedCountry}
      />

      <Modal
        show={showOptionsModal}
        onHide={handleCloseOptionsModal}
        centered
        className="text-light"
      >
        <Modal.Header closeButton className="bg-dark text-light border-0">
          <Modal.Title>{modalOptions.header}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-light">
          {modalOptions.body}
        </Modal.Body>
        <Modal.Footer className="bg-dark text-light border-0">
          <Button variant="secondary" onClick={handleCloseOptionsModal}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => modalOptions.method(modalOptions.data)}
          >
            {modalOptions.okay}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CountriesList;
